import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Send, ArrowRight } from "lucide-react";
import { debugLog } from "../lib/logger";
import { T } from "@/locales/tr";
import { PaymentNotice } from "@/components/PaymentNotice";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Transfer() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState("send_100");
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    sender_name: "",
    sender_email: "",
    recipient_name: "",
    recipient_phone: "",
    recipient_country: "Sénégal",
    payout_method: "wave",
    note: "",
  });

  useEffect(() => {
    axios.get(`${API}/transfer/packages`).then((r) => setPackages(r.data)).catch((e) => debugLog("packages:", e?.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Polling au retour de Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (sid) navigate(`/transfer/success?session_id=${sid}`, { replace: true });
  }, [navigate]);

  const submit = async () => {
    if (!form.sender_name || !form.sender_email || !form.recipient_name || !form.recipient_phone) {
      toast.error(T("Tous les champs nom & téléphone sont requis."));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/transfer/init`, {
        package_id: selected,
        origin_url: window.location.origin,
        user_id: (typeof window !== "undefined" && window.localStorage) ? window.localStorage.getItem("papicoo_user_id") : null,
        ...form,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error(T("Pas d'URL de paiement renvoyée."));
        setSubmitting(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || T("Échec — réessaie."));
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-transfer" className="max-w-5xl mx-auto px-4 sm:px-8 py-10">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-accent)]">
        {T("Transfert d'argent · Diaspora")}
      </div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[color:var(--papicoo-ink)] mt-2 leading-[0.98]">
        {T("Envoie au pays.")}<br />
        <span className="italic font-light">{T("Sans bruit, sans piège.")}</span>
      </h1>
      <div role="status" data-testid="transfer-unavailable" className="mt-6 papicoo-card p-4 text-sm text-[color:var(--papicoo-ink)] border-[color:var(--papicoo-gold)]/40">{T("Service indisponible pour le moment : les paiements en ligne sont fermés, aucun transfert ne peut être lancé. Tes transferts déjà payés restent honorés.")}</div>
      <p className="text-[color:var(--papicoo-muted)] mt-4 max-w-3xl leading-relaxed">
        {T("Paie par carte en sécurité")} <strong>{T("(Stripe)")}</strong>{T(". Papicoo s'occupe ensuite du payout vers")} <strong>{T("Wave")}</strong>, <strong>{T("Orange Money")}</strong> {T("ou")} <strong>{T("Wari")}</strong> {T("chez le destinataire. Frais transparents, ancrés dans l'")}<em>{T("amâna")}</em> {T("coranique.")}
      </p>
      <div className="mt-3">
        <a href="/me/transferts" data-testid="link-my-transfers" className="text-xs papicoo-link-underline">
          {T("→ Voir mes transferts précédents")}
        </a>
      </div>

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {packages.map((p) => (
          <button
            key={p.id}
            data-testid={`pkg-${p.id}`}
            onClick={() => setSelected(p.id)}
            className={`papicoo-card p-5 text-left transition ${
              selected === p.id
                ? "ring-2 ring-[color:var(--papicoo-gold)] bg-[color:var(--papicoo-sand-deep)]"
                : "hover:bg-[color:var(--papicoo-sand-deep)]/40"
            }`}
          >
            <div className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T(p.label)}</div>
            <div className="font-display text-3xl text-[color:var(--papicoo-ink)] mt-2">{p.send_eur} €</div>
            <div className="text-xs text-[color:var(--papicoo-muted)] mt-1">{T("arrivent au pays")}</div>
            <div className="mt-3 text-sm">
              {T("Frais Papicoo :")} <strong>{p.fee_eur} €</strong>
            </div>
            <div className="mt-1 text-sm text-[color:var(--papicoo-ink)]">
              {T("Total débité :")} <strong>{p.total_eur} €</strong>
            </div>
          </button>
        ))}
      </div>

      <div className="mt-8 papicoo-card p-6 grid gap-4 md:grid-cols-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Expéditeur")}</div>
          <input
            data-testid="sender-name"
            placeholder={T("Ton nom complet")}
            value={form.sender_name}
            onChange={(e) => setForm({ ...form, sender_name: e.target.value })}
            className="mt-2 w-full bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2.5 text-sm"
          />
          <input
            data-testid="sender-email"
            type="email"
            placeholder={T("Ton email (reçu)")}
            value={form.sender_email}
            onChange={(e) => setForm({ ...form, sender_email: e.target.value })}
            className="mt-2 w-full bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2.5 text-sm"
          />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Destinataire au Sénégal")}</div>
          <input
            data-testid="recipient-name"
            placeholder={T("Nom du destinataire")}
            value={form.recipient_name}
            onChange={(e) => setForm({ ...form, recipient_name: e.target.value })}
            className="mt-2 w-full bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2.5 text-sm"
          />
          <input
            data-testid="recipient-phone"
            placeholder={T("Téléphone Wave / Orange Money")}
            value={form.recipient_phone}
            onChange={(e) => setForm({ ...form, recipient_phone: e.target.value })}
            className="mt-2 w-full bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2.5 text-sm"
          />
          <select
            data-testid="payout-method"
            value={form.payout_method}
            onChange={(e) => setForm({ ...form, payout_method: e.target.value })}
            className="mt-2 w-full bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2.5 text-sm"
          >
            <option value="wave">{T("Wave")}</option>
            <option value="orange_money">{T("Orange Money")}</option>
            <option value="wari">{T("Wari")}</option>
          </select>
        </div>
        <textarea
          data-testid="note"
          rows={2}
          placeholder={T("Mot pour le destinataire (optionnel)")}
          value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
          className="md:col-span-2 w-full bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2.5 text-sm"
        />
        <button
          data-testid="transfer-submit"
          disabled
          aria-disabled="true"
          onClick={submit}
          className="md:col-span-2 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full papicoo-btn-primary text-sm disabled:opacity-50"
        >
          {submitting ? T("Redirection vers Stripe…") : (
            <>
              <Send size={16} /> {T("Payer en sécurité")}
              <ArrowRight size={16} />
            </>
          )}
        </button>
        <PaymentNotice className="md:col-span-2" />
      </div>
    </div>
  );
}
